/**
 * The sync-status indicator (`110-design-system.md` §9, `112-offline-drafts.md` §10).
 *
 * "It is the single most important piece of UI in the app after the editor itself, because
 * the first design value is *never lose data* — and a promise the user cannot see is a
 * promise they do not believe."
 *
 * Four states. Every one has a **distinct icon and distinct text**; colour is a
 * reinforcement, never the signal (§1.3), so the indicator is legible in greyscale and in
 * a screenshot. Nothing animates except the `RefreshCw` spin — a "Saved" that flashes
 * green on every keystroke is a distraction.
 */
import { useEffect, useState } from 'react';
import { Check, CloudOff, RefreshCw, TriangleAlert } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { useTranslation, type MessageKey } from '@/lib/i18n';
import type { OutboxEntry } from '@/offline/db';
import { discard, listEntries, retry, retryAll, subscribe } from '@/offline/outbox';
import { useSyncStatus } from '@/offline/useSyncStatus';

const KIND_LABEL_KEY: Record<string, MessageKey> = {
  'page.create': 'shell.sync.kind.create',
  'page.update': 'shell.sync.kind.update',
  'page.move': 'shell.sync.kind.move',
  'page.archive': 'shell.sync.kind.archive',
  'page.restore': 'shell.sync.kind.restore',
  'page.delete': 'shell.sync.kind.delete',
};

function age(
  createdAt: number,
  t: (key: MessageKey, params?: Record<string, string | number>) => string,
): string {
  const seconds = Math.round((Date.now() - createdAt) / 1000);
  if (seconds < 60) return t('shell.sync.age.seconds', { count: seconds });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('shell.sync.age.minutes', { count: minutes });
  return t('shell.sync.age.hours', { count: Math.round(minutes / 60) });
}

export function SyncIndicator() {
  const state = useSyncStatus();
  const { t, plural } = useTranslation();
  const [open, setOpen] = useState(false);
  const [entries, setEntries] = useState<OutboxEntry[]>([]);
  const [confirm, setConfirm] = useState<OutboxEntry | null>(null);

  useEffect(() => {
    const read = (): void => {
      void listEntries().then(setEntries);
    };
    read();
    return subscribe(read);
  }, []);

  const face = (() => {
    switch (state.kind) {
      case 'saved':
        return {
          icon: <Check className="size-3.5" strokeWidth={2} />,
          text: t('shell.sync.saved'),
          className: 'text-[var(--muted-foreground)]',
          title:
            state.lastSavedAt === null
              ? t('shell.sync.nothingPending')
              : t('shell.sync.savedAt', {
                  time: new Date(state.lastSavedAt).toLocaleTimeString(),
                }),
        };
      case 'saving':
        return {
          icon: <RefreshCw className="size-3.5 animate-spin" strokeWidth={2} />,
          text: t('shell.sync.saving'),
          className: 'text-[var(--muted-foreground)]',
          title: t('shell.sync.savingTitle'),
        };
      case 'offline':
        return {
          icon: <CloudOff className="size-3.5" strokeWidth={2} />,
          text: plural('shell.sync.pending', state.pending),
          className: 'text-[var(--warning)]',
          title:
            state.reason === 'unauthenticated'
              ? plural('shell.sync.signInToSync', state.pending)
              : t('shell.sync.offlineSaved'),
        };
      case 'conflict':
      default:
        return {
          icon: <TriangleAlert className="size-3.5" strokeWidth={2} />,
          text: t('shell.sync.conflict'),
          className: 'text-[var(--destructive)]',
          title: t('shell.sync.conflictTitle'),
        };
    }
  })();

  const failed = entries.filter((entry) => entry.status === 'failed');

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid="sync-status"
            title={face.title}
            className={cn(
              'flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs hover:bg-[var(--accent)]',
              face.className,
            )}
          >
            {face.icon}
            <span>{face.text}</span>
          </button>
        </PopoverTrigger>

        <PopoverContent align="start" className="w-96">
          {state.kind === 'offline' && state.reason === 'unauthenticated' ? (
            <p className="mb-2">{plural('shell.sync.signInToSync', state.pending)}</p>
          ) : state.kind === 'offline' ? (
            <p className="mb-2">{t('shell.sync.offlineNotice')}</p>
          ) : null}

          {entries.length === 0 ? (
            <p className="text-[var(--muted-foreground)]">{t('shell.sync.empty')}</p>
          ) : (
            <ul className="flex flex-col gap-1">
              {entries.map((entry) => (
                <li
                  key={entry.id}
                  className="flex items-start gap-2 rounded-sm border border-[var(--border)] p-1.5"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">
                      {entry.label ?? entry.entityId} —{' '}
                      {t(KIND_LABEL_KEY[entry.kind] ?? 'shell.sync.kind.update')}
                    </p>
                    <p className="text-xs text-[var(--muted-foreground)]">
                      {age(entry.createdAt, t)}
                      {entry.attempts > 0 && ` · ${plural('shell.sync.attempts', entry.attempts)}`}
                      {entry.status === 'failed' && ` · ${t('shell.sync.failed')}`}
                    </p>
                    {entry.lastError !== undefined && (
                      <p className="text-xs text-[var(--destructive)]">{entry.lastError.message}</p>
                    )}
                  </div>

                  {entry.status === 'failed' && (
                    <div className="flex shrink-0 gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          void retry(entry.id);
                        }}
                      >
                        {t('common.retry')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setConfirm(entry);
                        }}
                      >
                        {t('shell.sync.discard')}
                      </Button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {failed.length > 1 && (
            <Button
              size="sm"
              variant="outline"
              className="mt-2 w-full"
              onClick={() => {
                void retryAll();
              }}
            >
              {t('shell.sync.retryAll')}
            </Button>
          )}
        </PopoverContent>
      </Popover>

      {/* Discard names exactly what will be lost, and offers [Copy content] FIRST — so the
          text can be pasted somewhere before it goes. The only way content leaves the queue
          unsent is this dialog (`112` §5.4). */}
      <AlertDialog
        open={confirm !== null}
        onOpenChange={(next) => {
          if (!next) setConfirm(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('shell.sync.discardDialog.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === 'page.update'
                ? t('shell.sync.discardDialog.contentBody', {
                    label: confirm.label ?? t('shell.sync.discardDialog.thisPage'),
                  })
                : t('shell.sync.discardDialog.genericBody', {
                    kind:
                      confirm === null
                        ? ''
                        : t(KIND_LABEL_KEY[confirm.kind] ?? 'shell.sync.kind.update'),
                  })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            {confirm?.kind === 'page.update' && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const body = confirm.request.body;
                  void navigator.clipboard.writeText(JSON.stringify(body, null, 2));
                  toast.success(t('shell.sync.discardDialog.copiedToast'));
                }}
              >
                {t('shell.sync.discardDialog.copyContent')}
              </Button>
            )}
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const target = confirm;
                setConfirm(null);
                if (target !== null) void discard(target.id);
              }}
            >
              {t('shell.sync.discard')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
